/**
 * @file api-site-redirects.ts
 * @description Клиент «Сайт → SEO → Переадресации» (phase19.16, партия Р3в,
 * v0.19.64; SB131, SB132, SB128; контракт r3v-api-contract.md §1, §3).
 *
 * Цель переадресации — сущность магазина (страница, товар, категория,
 * статья, рубрика) или внутренний адрес: адрес сущности сервер вычисляет при
 * чтении, поэтому переименование цели ничего не ломает. Читать — право
 * контента или настроек; создавать, править и удалять — право настроек
 * (SB55, SB131).
 *
 * `list` возвращает строки и сводку (`total`, `auto`, `manual`, предел
 * ручных 1000, `nearLimit` с 900) — список после мутаций перечитывается, а
 * не правится руками.
 */

import { apiFetch } from "./api-client";

const BASE = "/admin/site/redirects";

/** Откуда взялась переадресация (`SiteRedirectSource`). */
export type SiteRedirectSource = "AUTO" | "MANUAL" | "IMPORT";

/** Вид цели (`SiteRedirectTargetType`). */
export type SiteRedirectTargetType = "PATH" | "SITE_PAGE" | "PRODUCT" | "CATEGORY" | "CONTENT_ARTICLE" | "CONTENT_CATEGORY";

/** Вид цели-сущности (без адреса). */
export type SiteRedirectEntityType = Exclude<SiteRedirectTargetType, "PATH">;

/** Цель в теле запроса (плоско, как в контракте). */
export interface SiteRedirectTargetInput {
  targetType: SiteRedirectTargetType;
  targetId?: string;
  targetPath?: string;
}

/** Действует ли правило (вычисляется сервером при чтении). */
export type SiteRedirectStatus = "active" | "target_hidden" | "target_deleted" | "source_live" | "loop";

/** Кто живёт по адресу. */
export type SiteLiveOwnerType = "page" | "product" | "category" | "article" | "blog-category" | "blog-tag" | "blog-author" | "section" | "service";

/** Цель в ответе: название и адрес на сайте. */
export interface SiteRedirectTarget {
  type: SiteRedirectTargetType;
  id: string | null;
  /** У адреса — как сохранён, с ?query. */
  path: string | null;
  /** «О нас», «Иван-чай»; null у адреса и удалённой цели. */
  title: string | null;
  /** Адрес цели на сайте сейчас; `null` — цели нет. */
  href: string | null;
}

/** Строка списка. */
export interface SiteRedirectItem {
  id: string;
  /** Приведённый адрес (нижний регистр, кириллица в %-кодировке). */
  fromPath: string;
  /** Тот же адрес для человека: `/старый-чай`. */
  fromDisplay: string;
  source: SiteRedirectSource;
  target: SiteRedirectTarget;
  status: SiteRedirectStatus;
  /** Кто живёт по адресу «откуда» (статус `source_live`). */
  liveOwner: { type: SiteLiveOwnerType; id: string; title: string } | null;
  createdAt: string;
  updatedAt: string;
  createdByName: string | null;
}

/** Сводка над списком (по всему магазину). */
export interface SiteRedirectSummary {
  total: number;
  auto: number;
  manual: number;
  imported: number;
  inactive: number;
  /** Предел ручных правил на магазин (1000, SB131). */
  manualLimit: number;
  manualLeft: number;
  /** Ручных ≥ 900 — полоса «Осталось N правил из 1000». */
  nearLimit: boolean;
}

export interface SiteRedirectPage {
  items: SiteRedirectItem[];
  nextCursor: string | null;
  summary: SiteRedirectSummary;
  /** «Сайт применит изменения в течение минуты». */
  applyNote: string;
}

/** Предупреждения записи — сохранено, но стоит сказать. */
export type SiteRedirectWarning = "site_redirect_target_hidden" | "site_redirect_limit_near";

export interface SiteRedirectWriteResult {
  redirect: SiteRedirectItem;
  warnings: SiteRedirectWarning[];
}

export type SiteRedirectFilter = "all" | "auto" | "manual" | "import";
export type SiteRedirectStatusFilter = "all" | "active" | "inactive";

export interface SiteRedirectListQuery {
  q?: string;
  source?: SiteRedirectFilter;
  status?: SiteRedirectStatusFilter;
  cursor?: string | null;
  limit?: number;
}

/** Прыжок ответа витрины. */
export interface SiteRedirectHop {
  url: string;
  status: number;
  location: string | null;
}

/** «Куда ведёт сейчас» (`POST …/probe`, контракт §1). */
export interface SiteRedirectProbeResult {
  input: string;
  normalized: string | null;
  kind: "invalid" | "service" | "section" | "page";
  liveOwner: { type: SiteLiveOwnerType; id: string | null; title: string } | null;
  rule: { id: string | null; source: SiteRedirectSource | "PLATFORM"; status: SiteRedirectStatus; location: string | null } | null;
  /** `status: null` — служебный адрес, ожидание не считается. */
  expected: { status: 200 | 301 | 404 | null; location: string | null };
  observed:
    | { ok: true; hops: SiteRedirectHop[]; finalStatus: number; finalUrl: string }
    | { ok: false; error: "web_not_configured" | "timeout" | "web_unavailable" | "too_many_hops"; hops: SiteRedirectHop[] };
  note: string;
}

/** Что сделали переадресации при смене адреса сущности (контракт §3). */
export interface SiteRedirectChange {
  created: { id: string; fromPath: string; fromDisplay: string }[];
  /** Сняты: живое содержимое снова заняло адрес. */
  removed: { id: string; fromPath: string; fromDisplay: string; source: SiteRedirectSource }[];
}

/** Размер страницы списка (контракт §1). */
export const SITE_REDIRECTS_PAGE = 50;

export const siteRedirects = {
  list: (query: SiteRedirectListQuery = {}): Promise<SiteRedirectPage> => {
    const q = new URLSearchParams({ limit: String(query.limit ?? SITE_REDIRECTS_PAGE) });
    if (query.q?.trim()) q.set("q", query.q.trim());
    if (query.source && query.source !== "all") q.set("source", query.source);
    if (query.status && query.status !== "all") q.set("status", query.status);
    if (query.cursor) q.set("cursor", query.cursor);
    return apiFetch<SiteRedirectPage>(`${BASE}?${q.toString()}`);
  },

  create: (input: { fromPath: string } & SiteRedirectTargetInput): Promise<SiteRedirectWriteResult> =>
    apiFetch<SiteRedirectWriteResult>(BASE, { method: "POST", json: input }),

  /** Правка автоматического правила делает его ручным (входит в предел). Цель — целиком. */
  update: (id: string, input: { fromPath?: string } & Partial<SiteRedirectTargetInput>): Promise<SiteRedirectWriteResult> =>
    apiFetch<SiteRedirectWriteResult>(`${BASE}/${encodeURIComponent(id)}`, { method: "PATCH", json: input }),

  remove: (id: string): Promise<{ id: string }> => apiFetch<{ id: string }>(`${BASE}/${encodeURIComponent(id)}`, { method: "DELETE" }),

  /** «Куда ведёт сейчас»: сервер спрашивает витрину (HEAD, до 5 прыжков по 3 с). */
  probe: (path: string): Promise<SiteRedirectProbeResult> => apiFetch<SiteRedirectProbeResult>(`${BASE}/probe`, { method: "POST", json: { path } }),
};
